import { FormattedMessage, useIntl } from "react-intl";
import styles from "./MyCartButton.module.scss";
import GlobalNavCart from "../../../../common/icons/GlobalNavCart.tsx";
import { useEffect, useRef } from "react";
import useMediaQuery from "common/core-ui/useMediaQuery.ts";
import { useTransition } from "react-transition-state";
import clsx from "clsx";
import { Link } from "react-router-dom";
import OffScreen from "common/core-ui/OffScreen.tsx";
import { HeaderTheme } from "./types.ts";

export default function MyCartButton({
  count,
  price,
  currency = "USD",
  to,
  theme,
}: {
  count: number;
  price: number | string;
  currency?: string;
  to: string;
  theme?: HeaderTheme;
}) {
  const intl = useIntl();
  const priceToNum = Number(price);
  const cartHasPrice = priceToNum > 0;
  const previousCount = useRef<number | undefined>(count);
  const viewPrice = intl.formatNumber(priceToNum, {
    style: "currency",
    currency,
  });
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );
  const [state, animate] = useTransition({ timeout: 1500 });
  const defaultText = intl.formatMessage({ defaultMessage: "My Cart" });
  const countStatement = intl.formatMessage(
    {
      defaultMessage: "{count, plural, =1 {1 item} other {# items}} in cart",
    },
    { count }
  );
  const priceStatement = intl.formatMessage(
    {
      defaultMessage: ", {viewPrice} cart total",
    },
    { viewPrice }
  );
  const cartDetails = `${countStatement}${cartHasPrice ? priceStatement : ""}`;

  useEffect(() => {
    if (count !== previousCount.current && !prefersReducedMotion) {
      animate();
    }
    previousCount.current = count;
  }, [count, prefersReducedMotion]);

  return (
    <>
      <OffScreen role="status" aria-live="polite">
        {cartDetails}
      </OffScreen>
      <Link
        className={clsx(
          styles.container,
          theme === "accent" && styles.accent,
          theme === "outline" && styles.outline
        )}
        to={to}
        aria-label={count ? cartDetails : defaultText}
      >
        <div
          className={clsx(
            styles.cartImageContainer,
            (state.status === "entering" || state.status === "exiting") &&
              styles.animate
          )}
        >
          <GlobalNavCart className={styles.cartImage} />
          <div
            className={clsx(
              styles.count,
              count === 0 ? styles.hideCount : styles.showCount,
              (state.status === "entering" || state.status === "exiting") &&
                styles.animate
            )}
          >
            {count}
          </div>
        </div>
        <div className={styles.cartText}>
          <div>{defaultText}</div>
          {cartHasPrice && (
            <div>
              <FormattedMessage
                defaultMessage={"({cost})"}
                values={{
                  cost: viewPrice,
                }}
              />
            </div>
          )}
        </div>
      </Link>
    </>
  );
}
